import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgClass, CommonModule } from '@angular/common';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SelectModule } from 'primeng/select';
import { ProgressBarComponent } from '../../ProgressBar/progressbar.component';
import { ToolsBoxService } from '../../Services/tools.service';
import { UserService } from '../../Services/user.service';
import { BalanceReportItem } from '../../Models/Holders/balanceReportHolder.model';

@Component({
  selector: 'app-reporting',
  templateUrl: 'reporting.component.html',
  styleUrls: ['reporting.component.css'],
  standalone: true,
  imports: [FormsModule, CommonModule, NgClass, SelectButtonModule, SelectModule, ProgressBarComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportingComponent implements OnInit {
  private readonly toolsBox = inject(ToolsBoxService);
  private readonly userService = inject(UserService);
  private readonly cdr = inject(ChangeDetectorRef);

  selectedYear = new Date().getFullYear();
  readonly yearOptions = [
    { label: String(new Date().getFullYear() - 2), value: new Date().getFullYear() - 2 },
    { label: String(new Date().getFullYear() - 1), value: new Date().getFullYear() - 1 },
    { label: String(new Date().getFullYear()), value: new Date().getFullYear() }
  ];

  sortMode: 'name' | 'leaves' | 'dol' = 'name';
  filterOption: { label: string; value: string; code: string } | null = null;
  items: BalanceReportItem[] = [];
  isLoading = false;
  readonly skeletonRows = Array(6);

  readonly countryOptions = [
    { label: 'Luxembourg', value: 'LUXEMBOURG', code: 'lu' },
    { label: 'France', value: 'FRANCE', code: 'fr' },
    { label: 'Belgique', value: 'BELGIQUE', code: 'be' },
    { label: 'Allemagne', value: 'ALLEMAGNE', code: 'de' }
  ];

  ngOnInit() {
    this.toolsBox.changeHeaderName('Reporting');
    this.toolsBox.tellChildContainsTabs(false);
    this.loadReport();
  }

  loadReport() {
    this.items = [];
    this.isLoading = true;
    this.toolsBox.tellActionIsInProgress(true);
    this.cdr.markForCheck();
    this.userService.getBalanceReport(this.selectedYear).subscribe({
      next: (data) => {
        this.items = data;
        this.isLoading = false;
        this.toolsBox.tellActionIsInProgress(false);
        this.cdr.markForCheck();
      },
      error: () => {
        this.isLoading = false;
        this.toolsBox.tellActionIsInProgress(false);
        this.cdr.markForCheck();
      }
    });
  }

  filteredAndSorted(): BalanceReportItem[] {
    let result = this.filterOption
      ? this.items.filter(i => i.user.taxResidence === this.filterOption!.value)
      : [...this.items];

    if (this.sortMode === 'name') {
      result.sort((a, b) =>
        (a.user.userName + a.user.userFirstName).localeCompare(b.user.userName + b.user.userFirstName)
      );
    } else if (this.sortMode === 'leaves') {
      result.sort((a, b) => this.leavesPercent(b) - this.leavesPercent(a));
    } else if (this.sortMode === 'dol') {
      result.sort((a, b) => this.dolPercent(b) - this.dolPercent(a));
    }

    return result;
  }

  leavesPercent(item: BalanceReportItem): number {
    if (!item.totalBalance) return 0;
    return Math.min(100, Math.round((item.takenCount / item.totalBalance) * 100));
  }

  dolPercent(item: BalanceReportItem): number {
    if (!item.maxDol) return 0;
    return Math.min(100, Math.round((item.dolCount / item.maxDol) * 100));
  }

  barColor(percent: number): string {
    if (percent >= 90) return '#e74c3c';
    if (percent >= 75) return '#e67e22';
    return '#3D9BC5';
  }

  alertLevel(item: BalanceReportItem): 'danger' | 'warning' | 'none' {
    const percents = this.selectedYear === 2024
      ? [this.dolPercent(item)]
      : [this.leavesPercent(item), this.dolPercent(item)];
    const max = Math.max(...percents);
    if (max >= 90) return 'danger';
    if (max >= 75) return 'warning';
    return 'none';
  }

  flagClass(taxResidence: string): string {
    const map: Record<string, string> = {
      FRANCE: 'fi-fr',
      LUXEMBOURG: 'fi-lu',
      BELGIQUE: 'fi-be',
      ALLEMAGNE: 'fi-de'
    };
    return map[taxResidence] ?? '';
  }
}
